import { useTranslation } from 'react-i18next';

import ExportDetails from '@features/campaigns/details/components/CampaignDetails/ImpressionsCalculator/ExportDetails/ExportDetails';
import { AvailabilityAndCostData } from '@features/campaigns/details/containers/CampaignDetailsPage/CampaignDetailsPage.dto';
import { AvailableImpressions } from '@features/campaigns/shared/components/ImpressionsCalculator/AvailableImpressions/AvailableImpressions';
import { Budget } from '@features/campaigns/shared/components/ImpressionsCalculator/Budget/Budget';
import { ItemRow } from '@features/campaigns/shared/components/ImpressionsCalculator/ItemRow/ItemRow';
import { ItemValue } from '@features/campaigns/shared/components/ImpressionsCalculator/ItemValue/ItemValue';
import { ItemValueRange } from '@features/campaigns/shared/components/ImpressionsCalculator/ItemValueRange/ItemValueRange';
import { SectionWrapper } from '@features/campaigns/shared/components/Review/SectionWrapper/SectionWrapper';
import { TargetSizeIcon } from '@features/campaigns/shared/components/TargetSizeIcon/TargetSizeIcon';
import { SuccessTag } from '@shared/components/Tag/SuccessTag/SuccessTag.tsx';
import { i18nNameSpace } from '@shared/consts/i18n.ts';

import styles from './AvailabilityAndCost.module.scss';

interface ImpressionsCalculatorProps {
  data?: AvailabilityAndCostData;
}

export default function AvailabilityAndCost({ data }: ImpressionsCalculatorProps) {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);

  const shouldDisplayProgressBar = Boolean(data?.impressions) && Boolean(data?.availableImpressions);
  const canShowBonusImpressions = Boolean(data?.bonusImpressions && data.bonusImpressions > 0) && !data?.bonusPrice;
  const canShowBonusCampaign = Boolean(data?.bonusPrice);
  const canShowInventoryCampaign = Boolean(data?.inventory);

  const estimatedReachMin = data?.estimatedReachRange ? data.estimatedReachRange[0] : null;
  const estimatedReachMax = data?.estimatedReachRange ? data.estimatedReachRange[1] : null;
  const estimatedFrequencyMin = data?.estimatedFrequencyRange ? data.estimatedFrequencyRange[0] : null;
  const estimatedFrequencyMax = data?.estimatedFrequencyRange ? data.estimatedFrequencyRange[1] : null;

  return (
    <SectionWrapper
      headerText={'page.campaignDetails.campaignDetailsTab.sectionTitles.availabilityAndCost'}
      additionalAction={<ExportDetails />}
    >
      {shouldDisplayProgressBar && (
        <AvailableImpressions value={data!.impressions!} maxValue={data!.availableImpressions!} />
      )}

      {canShowInventoryCampaign && (
        <ItemRow labelText={t('availabilityAndCosts.inventoryCampaign')} iconClass={'pi-images'}>
          <SuccessTag message={t('availabilityAndCosts.active')} className={styles.tagText} />
        </ItemRow>
      )}

      {canShowBonusCampaign && (
        <ItemRow labelText={t('availabilityAndCosts.bonusCampaign')} iconClass={'pi-gift'}>
          <SuccessTag message={t('availabilityAndCosts.active')} className={styles.tagText} />
        </ItemRow>
      )}

      <ItemRow labelText={t('availabilityAndCosts.impressions')} iconClass={'pi-eye'}>
        <ItemValue value={data?.impressions} />
      </ItemRow>
      {canShowBonusImpressions && (
        <ItemRow
          labelText={t('availabilityAndCosts.bonusImpressions')}
          iconClass={'pi-gift'}
          tooltipText={t('availabilityAndCosts.bonusImpressionsTooltip')}
        >
          <ItemValue value={data?.bonusImpressions} />
        </ItemRow>
      )}
      <ItemRow
        labelText={t('availabilityAndCosts.estimatedReach')}
        iconClass={'pi-users'}
        tooltipText={t('availabilityAndCosts.estimatedReachTooltip')}
      >
        <ItemValueRange isRounded minValue={estimatedReachMin} maxValue={estimatedReachMax} />
      </ItemRow>
      <ItemRow
        labelText={t('availabilityAndCosts.targetSize')}
        tooltipText={t('availabilityAndCosts.targetSizeTooltip')}
        customIcon={<TargetSizeIcon />}
      >
        <ItemValue value={data?.targetSize} />
      </ItemRow>
      <ItemRow
        labelText={t('availabilityAndCosts.estimatedFrequency')}
        iconClass={'pi-users'}
        tooltipText={t('availabilityAndCosts.estimatedFrequencyTooltip')}
      >
        <ItemValueRange minValue={estimatedFrequencyMin} maxValue={estimatedFrequencyMax} />
      </ItemRow>
      <ItemRow labelText={t('availabilityAndCosts.CPM')} iconClass={'pi-money-bill'}>
        <ItemValue value={data?.cpm} moneyFormat />
      </ItemRow>

      <Budget readOnlyValue={data?.budget || 0} />
    </SectionWrapper>
  );
}
